import type { ButtonHTMLAttributes, ReactNode } from "react";

import { tokens } from "../tokens/tokens";

export type ButtonVariant = "primary" | "secondary" | "danger" | "ghost";
export type ButtonSize = "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Render full-width inside its parent. */
  fullWidth?: boolean;
  children: ReactNode;
}

/**
 * Primary tap target across the apps. Sized to NFR-A11Y-02 (48 dp minimum;
 * 56 dp for gate-kiosk surfaces via the `lg` variant). Native button so it
 * inherits the global :focus-visible ring from global.css.
 *
 * Visual:
 *   primary   — brand gradient, subtle inner highlight, lifts on hover
 *   secondary — outlined, neutral, becomes filled on hover
 *   danger    — solid red
 *   ghost     — transparent, brand-text — for tertiary actions in a row
 */
export function Button({
  variant = "primary",
  size = "md",
  fullWidth = false,
  style,
  children,
  ...rest
}: ButtonProps) {
  const isLg = size === "lg";
  const minHeight = isLg ? tokens.size.tap56 : tokens.size.tap48;

  const base: React.CSSProperties = {
    minHeight,
    minWidth: minHeight,
    width: fullWidth ? "100%" : undefined,
    padding: `0 ${isLg ? tokens.space[5] : tokens.space[4]}`,
    borderRadius: tokens.radius.lg,
    fontSize: isLg ? tokens.type.size.base : tokens.type.size.sm,
    fontWeight: tokens.type.weight.semibold,
    letterSpacing: "0.01em",
    cursor: rest.disabled ? "not-allowed" : "pointer",
    opacity: rest.disabled ? 0.55 : 1,
    transition: `transform ${tokens.motion.durFast} ${tokens.motion.easeOut}, box-shadow ${tokens.motion.durBase} ${tokens.motion.easeOut}, background ${tokens.motion.durBase} ${tokens.motion.easeOut}, color ${tokens.motion.durBase} ${tokens.motion.easeOut}`,
    border: 0,
  };

  let variantStyle: React.CSSProperties;
  switch (variant) {
    case "primary":
      variantStyle = {
        background: `linear-gradient(180deg, ${tokens.color.brand[500]} 0%, ${tokens.color.brand[600]} 100%)`,
        color: tokens.color.neutral[0],
        boxShadow: rest.disabled
          ? "none"
          : `0 1px 2px rgba(15, 23, 42, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.15)`,
      };
      break;
    case "danger":
      variantStyle = {
        background: tokens.color.danger[500],
        color: tokens.color.neutral[0],
      };
      break;
    case "ghost":
      variantStyle = {
        background: "transparent",
        color: tokens.color.brand[600],
        fontWeight: tokens.type.weight.medium,
      };
      break;
    case "secondary":
    default:
      variantStyle = {
        background: tokens.color.neutral[0],
        color: tokens.color.semantic.text,
        border: `1px solid ${tokens.color.semantic.borderStrong}`,
      };
      break;
  }

  return (
    <button
      {...rest}
      style={{ ...base, ...variantStyle, ...style }}
      onMouseDown={(e) => {
        if (!rest.disabled) {
          e.currentTarget.style.transform = "scale(0.985)";
        }
        rest.onMouseDown?.(e);
      }}
      onMouseUp={(e) => {
        e.currentTarget.style.transform = "";
        rest.onMouseUp?.(e);
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "";
        rest.onMouseLeave?.(e);
      }}
    >
      {children}
    </button>
  );
}
